import api from './api';

// Helper function để tạo headers với token
const getAuthHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

// Notifications API functions
export const notificationsApi = {
  // Lấy tất cả notifications cho user hiện tại
  getNotifications: async (token, params = {}) => {
    try {
      const response = await api.get('/notifications', {
        headers: getAuthHeaders(token),
        params
      });
      return response;
    } catch (error) {
      console.error('Error getting notifications:', error);
      throw error;
    }
  },

  // Lấy unread notifications count
  getUnreadCount: async (token) => {
    try {
      const response = await api.get('/notifications/unread-count', {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error getting unread count:', error);
      throw error;
    }
  },

  // Mark notification as read
  markAsRead: async (token, notificationId) => {
    try {
      const response = await api.put(`/notifications/${notificationId}/read`, {}, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  // Mark all notifications as read
  markAllAsRead: async (token) => {
    try {
      const response = await api.put('/notifications/read-all', {}, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  // Delete notification
  deleteNotification: async (token, notificationId) => {
    try {
      const response = await api.delete(`/notifications/${notificationId}`, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  },

  // Delete all notifications
  deleteAllNotifications: async (token) => {
    try {
      const response = await api.delete('/notifications/delete-all', {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      throw error;
    }
  },

  // Get notification settings
  getNotificationSettings: async (token) => {
    try {
      const response = await api.get('/notifications/settings', {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error getting notification settings:', error);
      throw error;
    }
  },

  // Update notification settings
  updateNotificationSettings: async (token, settings) => {
    try {
      const response = await api.put('/notifications/settings', settings, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  }
};

// WebSocket connection helper
export class NotificationWebSocket {
  constructor(token, onMessage, onError, onClose) {
    this.token = token;
    this.onMessage = onMessage;
    this.onError = onError;
    this.onClose = onClose;
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
  }

  connect() {
    try {
      // Auto-detect WebSocket URL based on environment
      const getWsUrl = () => {
        if (process.env.REACT_APP_WS_URL) {
          return process.env.REACT_APP_WS_URL;
        }
        
        // In production, use the same domain as the current page
        if (process.env.NODE_ENV === 'production') {
          const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          const host = window.location.host;
          return `${protocol}//${host}`;
        }
        
        // Default to localhost for development
        return 'ws://localhost:3001';
      };
      
      const wsUrl = getWsUrl();
      this.socket = new WebSocket(`${wsUrl}/notifications?token=${this.token}`);

      this.socket.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.onMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.onError(error);
      };

      this.socket.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this.onClose(event);
        
        // Auto reconnect logic
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          setTimeout(() => {
            this.reconnectAttempts++;
            this.connect();
          }, this.reconnectDelay * this.reconnectAttempts);
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.onError(error);
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  send(message) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  }

  // Subscribe to specific notification types
  subscribe(type) {
    this.send({
      type: 'subscribe',
      notificationType: type
    });
  }

  // Unsubscribe from specific notification types
  unsubscribe(type) {
    this.send({
      type: 'unsubscribe',
      notificationType: type
    });
  }
}

export default notificationsApi; 